import { takeUntil } from 'rxjs/operators';
import { Component, EventEmitter, Output, input } from '@angular/core';

import { DoodProductModel } from '@core/models/product.model';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderColorValue } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ProductPushV2AtomHoverColors } from '@shared/interfaces/product-hover-colors.interface';
import { CardCarouselComponent } from '@shared/components/card-carousel/card-carousel.component';
import { ProductCardAtomComponent } from '../product-card-atom/product-card-atom.component';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { desktopCarouselSlotCount } from './product-card-list-carousel-layout';

@Component({
  selector: 'app-product-card-list-atom',
  templateUrl: './product-card-list-atom.component.html',
  imports: [CardCarouselComponent, ProductCardAtomComponent, ContentBuilderColorPipe],
})
export class ProductCardListAtomComponent extends DestroyerBase {
  readonly displayContext = input('');
  readonly cta = input<string>();
  readonly heading = input<string>();
  readonly description = input<string>();
  readonly products = input.required<DoodProductModel[]>();
  readonly isOrderingAllowed = input(true);
  readonly categoryNameColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly categoryDescriptionColor = input<ContentBuilderColorValue>({
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productNameColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productDescriptionColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productPriceColor = input<ContentBuilderColorValue>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productImagePlaceholderColor = input<ContentBuilderColorValue>({
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productBorderColor = input<ContentBuilderColorValue>({
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  });
  readonly productBackgroundColor = input<ContentBuilderColorValue>({
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  });
  readonly productHoverColors = input<ProductPushV2AtomHoverColors | undefined>(undefined);
  /** When set, overrides {@link desktopCarouselSlotCount} for the desktop carousel grid. */
  readonly desktopVisibleCards = input<number | undefined>(undefined);

  @Output() productClick = new EventEmitter<DoodProductModel>();
  @Output() ctaClick = new EventEmitter();

  isDesktop = false;

  constructor(protected settingsSelector: SettingsStoreSelector) {
    super();

    this.settingsSelector.selectDeviceIsDesktopScreen
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(_isDesktop => (this.isDesktop = _isDesktop));
  }

  desktopCarouselSlotCount(): number {
    return desktopCarouselSlotCount(this.displayContext(), this.desktopVisibleCards());
  }

  getMobileCardClass(): string {
    const base = 'mr-3 md:mr-4 lg:mr-6 xl:mr-8 flex-shrink-0 flex-grow-0';

    if (this.displayContext() === 'shop_page') {
      return `${base} min-w-[72%] w-[72%] sm:min-w-64 sm:w-64`;
    }

    const defaultWidth = 'min-w-10 w-10 lg:min-w-52 lg:w-52';

    if (this.displayContext() !== 'promotion_block') {
      return `${base} ${defaultWidth}`;
    }

    return `${base} min-w-[30%] w-[30%]`;
  }
}
